import {
  DEBOUNCE_HISTORY_VERSION_TIME_PERIOD,
  FeedbackTab,
} from '@chegg-wt/core/constants/paraphraser'
import { Step } from '@tiptap/pm/transform'
import { Dispatch } from 'redux'
import {
  appendFeedback,
  deleteFeedback,
  setActiveFeedbackCardId,
  setActiveFeedbackTab,
} from '@chegg-wt/core/state/paraphraser/actions'
import {
  activeFeedbackTabSelector,
  feedbacksSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core/state/paraphraser/selectors'
import { Feedback, FeedbackType } from '@chegg-wt/core/types/paraphraser'
import { AppState } from '@chegg-wt/core/state'
import {
  adjustFeedbacksInRedux,
  setNextCardActiveAfterDeleteOrUpdate,
} from '@chegg-wt/core/components/ProofReader/CardsContainer/SuggestionTabs/utils'
import { getFeedbackPosition } from '../helpers'
import { IS_HISTORY_CHANGE } from '../constants'
import getFeatureEditor from '../getEditor'
import {
  FeedbackDelta,
  HistoryCurrentVersion,
  HistoryDeltaAction,
  HistoryVersion,
} from './types'
import history from './history'

export const debounceHelper = (func: Function, delay: number) => {
  let timeout
  return (callNow: boolean = false, ...args) => {
    const after = () => {
      timeout = null
      func(...args)
    }
    if (timeout) clearTimeout(timeout)
    if (callNow) func(...args)
    else timeout = setTimeout(after, delay)
  }
}

export const debounceOnCommitHistory = debounceHelper(
  () => history.commit(),
  DEBOUNCE_HISTORY_VERSION_TIME_PERIOD
)

export const mergeSteps = (steps: Step[]): Step[] => {
  if (steps.length < 2) return steps
  const merged = steps.reduce((mergedSteps, step) => {
    const lastStep = mergedSteps[mergedSteps.length - 1]
    const mergedStep = lastStep ? lastStep.merge(step) : null
    if (mergedStep) {
      /* eslint-disable no-param-reassign */
      mergedSteps[mergedSteps.length - 1] = mergedStep
    } else {
      mergedSteps.push(step)
    }
    return mergedSteps
  }, [])
  return merged
}

export const getHistoryVersionWithStepInJson = (
  currentData: HistoryCurrentVersion
): HistoryVersion => {
  const { steps, ...historyData } = {
    ...currentData,
    stepsJson: currentData.steps.map((step) => step.toJSON()),
  }
  return historyData
}

export const applyStepsAndGetInversion = (stepsJson: JSON[]): JSON[] => {
  const editor = getFeatureEditor()
  const invertedSteps = stepsJson.map((stepJson) => {
    const { state, view } = editor
    const oldDoc = state.doc
    const step = Step.fromJSON(state.schema, stepJson)
    const transaction = state.tr
      .step(step)
      .setMeta(IS_HISTORY_CHANGE, true)
      .scrollIntoView()
    view.dispatch(transaction)

    // Set the cursor at the end of the document after the step
    editor
      .chain()
      .focus()
      .setTextSelection(transaction.mapping.map((stepJson as any).to))
      .run()
    const inverted = step.invert(oldDoc).toJSON()
    return inverted
  })
  return invertedSteps
}

const isSameTabOpen = (
  feedbackType: FeedbackType,
  activeFeedbackTab: FeedbackTab
): boolean => {
  return (
    (feedbackType === FeedbackType.GRAMMAR &&
      activeFeedbackTab === FeedbackTab.GRAMMAR_TAB) ||
    (feedbackType === FeedbackType.PARAPHRASE &&
      activeFeedbackTab === FeedbackTab.PARAPHRASE_TAB)
  )
}

const openSameTab = (
  feedbackType: FeedbackType,
  activeFeedbackTab: FeedbackTab,
  dispatch: Dispatch
) => {
  if (!isSameTabOpen(feedbackType, activeFeedbackTab)) {
    dispatch(
      setActiveFeedbackTab(
        feedbackType === FeedbackType.GRAMMAR
          ? FeedbackTab.GRAMMAR_TAB
          : FeedbackTab.PARAPHRASE_TAB
      )
    )
  }
}

const selectNextCard = (
  feedbacks: Feedback[],
  removedFeedback: Feedback,
  dispatch: Dispatch
) => {
  const filteredFeedbacks = feedbacks.filter(
    (feedback) => feedback.feedbackType === removedFeedback.feedbackType
  )
  const removedIndex = filteredFeedbacks.indexOf(removedFeedback)
  setNextCardActiveAfterDeleteOrUpdate(removedIndex, filteredFeedbacks, (id) =>
    dispatch(setActiveFeedbackCardId(id))
  )
}

export const undoFeedbackDelta = (
  feedbackDelta: FeedbackDelta[],
  dispatch: Dispatch,
  state: AppState
) => {
  const editor = getFeatureEditor()
  feedbackDelta.forEach((delta) => {
    const activeFeedbackTab = activeFeedbackTabSelector(state)
    const sentenceOffsets = sentenceOffsetsSelector(state)
    const feedbackPosition = getFeedbackPosition(delta.feedback, sentenceOffsets)
    editor.commands.clearAllHighlights()
    if (
      delta.action === HistoryDeltaAction.APPLY &&
      delta.feedback.feedbackType === FeedbackType.GRAMMAR
    ) {
      // We need to adjust grammar offsets in this case
      const feedbacks = feedbacksSelector(state)
      const appliedFeedback = {
        ...delta.feedback,
        impactedText: delta.feedback.suggestedText,
      }
      appliedFeedback.suggestedText = ' '.repeat(appliedFeedback.position.length) // Only length is used in adjustFeedbacksInRedux, text not matter
      adjustFeedbacksInRedux(feedbacks, appliedFeedback, dispatch)
    }
    dispatch(appendFeedback([delta.feedback]))
    openSameTab(delta.feedback.feedbackType, activeFeedbackTab, dispatch)
    const endCursorPosition = feedbackPosition.offset + feedbackPosition.length
    editor
      .chain()
      .setCustomUnderline({
        id: delta.feedback.id,
        offset: feedbackPosition.offset,
        length: feedbackPosition.length,
        type: delta.feedback.feedbackType,
      })
      .setTextSelection({ from: endCursorPosition, to: endCursorPosition })
      .run()
    dispatch(setActiveFeedbackCardId(delta.feedback.id))
  })
}

export const redoFeedbackDelta = (
  feedbackDelta: FeedbackDelta[],
  dispatch: Dispatch,
  state: AppState
) => {
  const editor = getFeatureEditor()
  feedbackDelta.forEach((delta) => {
    editor.commands.clearAllHighlights()
    const activeFeedbackTab = activeFeedbackTabSelector(state)
    if (
      delta.action === HistoryDeltaAction.APPLY &&
      delta.feedback.feedbackType === FeedbackType.GRAMMAR
    ) {
      // We need to adjust grammar offsets in this case
      const feedbacks = feedbacksSelector(state)
      const appliedFeedback = {
        ...delta.feedback,
        impactedText: ' '.repeat(delta.feedback.position.length), // Only length is used in adjustFeedbacksInRedux, text not matter
      }
      adjustFeedbacksInRedux(feedbacks, appliedFeedback, dispatch)
    }
    dispatch(deleteFeedback(delta.feedback.id))
    openSameTab(delta.feedback.feedbackType, activeFeedbackTab, dispatch)
    const feedbacks = feedbacksSelector(state)
    selectNextCard(feedbacks, delta.feedback, dispatch)
  })
}
